import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Rewind,
  Stop,
  Pause,
  Play,
  SkipForward,
  DownloadSimple,
  SpeakerX,
  SpeakerLow,
  SpeakerHigh,
  GearSix,
  Warning,
  X,
  SpinnerGap,
  ListBullets,
} from "@phosphor-icons/react";
import {
  useOpenFile,
  useProjectId,
  useStoreDispatch,
  useTtsFromLine,
} from "../store/StoreContext.js";
import { useAuth } from "../context/AuthContext.js";
import { TtsSettingsForm } from "./TtsSettingsForm.js";
import { resolveTtsSettings } from "../../shared/tts.js";
import { registerTtsPlayFrom } from "../lib/ttsCommands.js";
import {
  useTtsPlaybackController,
  type ChunkAudioStatus,
  type ChunkTimingStatus,
} from "../lib/useTtsPlaybackController.js";

const PASTELS = [
  ["#f5e6d3", "#e8d5c4", "#dbc4b0", "#c4956a"],
  ["#e5eed8", "#d4e0c8", "#c3d2b8", "#93b573"],
  ["#e0daf5", "#d0c8eb", "#c0b6e0", "#9a85c9"],
  ["#f5d6d6", "#ebc4c4", "#e0b0b0", "#c97a7a"],
  ["#d6eef5", "#c4e0eb", "#b0d0e0", "#7ab0c9"],
  ["#f5ecd6", "#ebdec4", "#e0d0b0", "#c9b07a"],
];

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function drawAlbumArt(canvas: HTMLCanvasElement, text: string) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const size = canvas.width;
  const h = hashStr(text);
  const palette = PASTELS[h % PASTELS.length];

  ctx.fillStyle = palette[0];
  ctx.fillRect(0, 0, size, size);

  let seed = h;
  const rand = () => {
    seed = (seed * 16807 + 0) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  const blobCount = 4 + (h % 4);
  for (let i = 0; i < blobCount; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = size * 0.15 + rand() * size * 0.25;
    const color = palette[1 + (i % (palette.length - 1))];
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, color + "aa");
    gradient.addColorStop(1, color + "00");
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.lineWidth = 1.5;
  ctx.strokeStyle = palette[3] + "44";
  for (let l = 0; l < 3; l++) {
    ctx.beginPath();
    let lx = rand() * size;
    let ly = rand() * size;
    ctx.moveTo(lx, ly);
    for (let s = 0; s < 6; s++) {
      const cx1 = lx + (rand() - 0.5) * size * 0.5;
      const cy1 = ly + (rand() - 0.5) * size * 0.3;
      lx = rand() * size;
      ly = rand() * size;
      ctx.quadraticCurveTo(cx1, cy1, lx, ly);
    }
    ctx.stroke();
  }

  for (let d = 0; d < 12; d++) {
    const dx = rand() * size;
    const dy = rand() * size;
    const dr = 1 + rand() * 2;
    ctx.fillStyle = palette[3] + "55";
    ctx.beginPath();
    ctx.arc(dx, dy, dr, 0, Math.PI * 2);
    ctx.fill();
  }
}

function AlbumArt({ text }: { text: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const key = useMemo(() => hashStr(text || ""), [text]);

  useEffect(() => {
    if (canvasRef.current) drawAlbumArt(canvasRef.current, text || "");
  }, [key, text]);

  return (
    <canvas
      ref={canvasRef}
      width={80}
      height={80}
      className="w-10 h-10 rounded-md shrink-0"
    />
  );
}

function audioStatusLabel(status: ChunkAudioStatus): string {
  switch (status) {
    case "pending":
      return "pending";
    case "preparing":
      return "preparing";
    case "ready-cached":
      return "cached";
    case "playing":
      return "playing";
    case "error":
      return "error";
  }
}

function audioStatusTone(status: ChunkAudioStatus): string {
  switch (status) {
    case "playing":
      return "text-accent";
    case "ready-cached":
      return "text-success";
    case "error":
      return "text-danger";
    default:
      return "text-text-muted";
  }
}

function timingStatusTone(status: ChunkTimingStatus): string {
  switch (status) {
    case "ready":
      return "text-success";
    case "missing":
      return "text-danger";
    case "pending":
      return "text-text-muted";
  }
}

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export function AudioBar({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const { path: openFilePath, content } = useOpenFile();
  const projectId = useProjectId();
  const storeDispatch = useStoreDispatch();
  const ttsFromLine = useTtsFromLine();
  const { preferences: userPrefs, updatePreferences } = useAuth();
  const [showSettings, setShowSettings] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  const onTtsSettingsChange = useCallback(
    (settings: import("../../shared/tts.js").TtsSettings) => {
      void updatePreferences((current) => ({
        ...current,
        tts: settings,
      }));
    },
    [updatePreferences]
  );

  const controller = useTtsPlaybackController({
    openFilePath,
    content,
    projectId,
    userPrefs,
    ttsFromLine,
    storeDispatch,
    onTtsSettingsChange,
  });

  const {
    state,
    ttsSettings,
    setTtsSettings,
    clearError,
    setVolume,
    play,
    pause,
    stop,
    stopPreview,
    skipBack15,
    skipNextChunk,
    seekToGlobalTime,
    previewVoice,
    downloadAudio,
    regenerate,
    effectiveDuration,
    hasContent,
    isMainActive,
    currentVoiceName,
    providerName,
    chunkPlan,
    chunkStatuses,
    chunkDurations,
    currentChunkIndex,
    currentChunkNumber,
    preparedChunkCount,
    timestampReadyCount,
    downloadBusy,
    followAlong,
  } = controller;

  useEffect(() => {
    registerTtsPlayFrom(controller.playFrom);
    return () => registerTtsPlayFrom(null);
  }, [controller.playFrom]);

  useEffect(() => {
    if (!showSettings && !showDetails) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inSettings = settingsRef.current?.contains(target);
      const inDetails = detailsRef.current?.contains(target);
      if (!inSettings && !inDetails) {
        setShowSettings(false);
        setShowDetails(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSettings, showDetails]);

  useEffect(() => {
    if (!showSettings) {
      stopPreview();
    }
  }, [showSettings, stopPreview]);

  const isLoading = state.activeOutput === "main" && state.status === "loading";
  const hasDuration = Number.isFinite(effectiveDuration) && effectiveDuration > 0;
  const fileName = openFilePath?.split("/").pop() ?? "No file";

  if (!visible) return null;

  return (
    <div className="h-16 bg-surface border-t border-border shrink-0 flex items-center px-4 gap-4">
      <div className="flex items-center gap-3 w-56 min-w-0">
        <div className="relative">
          <AlbumArt text={content || ""} />
          {state.activity && (
            <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent activity-pulse" />
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm text-text font-medium truncate">{fileName}</div>
          <div className="text-xs truncate">
            {state.error ? (
              <button onClick={clearError} className="text-danger hover:underline">
                Error &middot; Click to dismiss
              </button>
            ) : state.activity ? (
              <span className="text-accent">{state.activity}</span>
            ) : (
              <span className="text-text-muted">
                {currentVoiceName} &middot; {providerName}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center gap-0.5 max-w-lg mx-auto">
        <div className="flex items-center gap-3">
          <button
            onClick={skipBack15}
            disabled={!isMainActive}
            className="text-text-muted hover:text-text transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            title="Back 15 seconds"
          >
            <Rewind size={16} />
          </button>

          <button
            onClick={stop}
            disabled={!isMainActive}
            className="text-text-muted hover:text-text transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            title="Stop"
          >
            <Stop size={14} weight="fill" />
          </button>

          {state.activeOutput === "main" && state.status === "playing" ? (
            <button
              onClick={pause}
              className="w-8 h-8 rounded-full bg-text text-surface flex items-center justify-center hover:scale-105 transition-transform"
              title="Pause"
            >
              <Pause size={14} weight="fill" />
            </button>
          ) : (
            <button
              onClick={play}
              disabled={!hasContent || isLoading}
              className="w-8 h-8 rounded-full bg-text text-surface flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              title="Read aloud"
            >
              {isLoading ? (
                <SpinnerGap size={14} className="animate-spin" />
              ) : (
                <Play size={14} weight="fill" />
              )}
            </button>
          )}

          <button
            onClick={skipNextChunk}
            disabled={!isMainActive}
            className="text-text-muted hover:text-text transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            title="Skip to next paragraph"
          >
            <SkipForward size={14} weight="fill" />
          </button>
        </div>

        <div className="flex items-center gap-2 w-full">
          <span className="text-[10px] text-text-muted tabular-nums w-8 text-right shrink-0">
            {isMainActive ? fmt(state.currentTime) : "0:00"}
          </span>
          <div
            className="flex-1 h-1 bg-border/50 rounded-full cursor-pointer group relative"
            onClick={(e) => {
              if (!hasDuration) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
              seekToGlobalTime(ratio * effectiveDuration);
            }}
          >
            {isLoading ? (
              <div className="h-full w-full overflow-hidden rounded-full">
                <div
                  className="h-full w-1/3 bg-accent rounded-full"
                  style={{
                    animation: "indeterminate-slide 1.5s ease-in-out infinite",
                  }}
                />
              </div>
            ) : (
              <>
                <div
                  className="h-full bg-accent rounded-full transition-[width] duration-100"
                  style={{ width: `${state.progress * 100}%` }}
                />
                {isMainActive && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-accent rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow"
                    style={{ left: `${state.progress * 100}%`, marginLeft: "-5px" }}
                  />
                )}
              </>
            )}
          </div>
          <span className="text-[10px] text-text-muted tabular-nums w-8 shrink-0">
            {hasDuration ? fmt(effectiveDuration) : "0:00"}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3 w-56 justify-end">
        <button
          onClick={() => void downloadAudio()}
          disabled={!hasContent || downloadBusy}
          className="text-text-muted hover:text-text transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
          title={downloadBusy ? "Preparing full MP3..." : "Download full MP3"}
        >
          {downloadBusy ? (
            <SpinnerGap size={16} className="animate-spin" />
          ) : (
            <DownloadSimple size={16} />
          )}
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setVolume((current) => (current > 0 ? 0 : 1))}
            className="text-text-muted hover:text-text transition-colors"
            title={state.volume > 0 ? "Mute" : "Unmute"}
          >
            {state.volume === 0 ? (
              <SpeakerX size={16} />
            ) : state.volume < 0.5 ? (
              <SpeakerLow size={16} />
            ) : (
              <SpeakerHigh size={16} />
            )}
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={state.volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-20 h-1 bg-border rounded-full appearance-none cursor-pointer accent-accent"
          />
        </div>

        <button
          onClick={() =>
            setTtsSettings((current) =>
              resolveTtsSettings({ ...current, followAlong: !current.followAlong })
            )
          }
          aria-pressed={followAlong}
          className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
            followAlong
              ? "border-accent/40 text-accent bg-accent/15 shadow-[inset_0_0_0_1px_rgba(196,149,106,0.15)]"
              : "border-border/90 text-text-muted bg-surface-alt/50 opacity-70 hover:opacity-100 hover:text-text"
          }`}
          title={followAlong ? "Follow-along enabled (click to disable)" : "Follow-along disabled (click to enable)"}
        >
          Follow
        </button>

        <div className="relative">
          <button
            onClick={() => setShowSettings((s) => !s)}
            className={`text-text-muted hover:text-text transition-colors ${showSettings ? "text-accent" : ""}`}
            title="Voice settings"
          >
            <GearSix size={16} />
          </button>

          {state.error && (
            <div className="absolute right-0 bottom-full mb-2 w-72 bg-surface border border-danger/30 rounded-lg shadow-lg p-4 z-50">
              <div className="flex items-start gap-3">
                <Warning size={20} className="text-danger shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text mb-1">Playback Error</div>
                  <div className="text-xs text-text-muted break-words">{state.error}</div>
                </div>
                <button
                  onClick={clearError}
                  className="text-text-muted hover:text-text transition-colors shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          {showSettings && (
            <div
              ref={settingsRef}
              className="absolute right-0 bottom-full mb-2 w-[22rem] bg-surface border border-border rounded-lg shadow-lg p-3 z-50 max-h-[32rem] overflow-y-auto"
            >
              <TtsSettingsForm
                value={ttsSettings}
                onChange={setTtsSettings}
                compact
                previewingKey={state.previewingVoice}
                onPreviewVoice={(request) => void previewVoice(request)}
                onRegenerate={() => {
                  setShowSettings(false);
                  regenerate();
                }}
                regenerateDisabled={!hasContent}
              />
            </div>
          )}
        </div>

        <div className="relative" ref={detailsRef}>
          <button
            onClick={() => setShowDetails((s) => !s)}
            className={`text-text-muted hover:text-text transition-colors ${showDetails ? "text-accent" : ""}`}
            title="Chunk details"
          >
            <ListBullets size={16} />
          </button>
          {showDetails && (
            <div className="absolute right-0 bottom-full mb-2 w-80 bg-surface border border-border rounded-lg shadow-lg p-3 z-50 max-h-80 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-text">Playback Details</div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-text-muted hover:text-text transition-colors"
                  title="Close details"
                >
                  <X size={12} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] mb-2">
                <div className="text-text-muted">
                  Chunks: <span className="text-text">{chunkPlan.length}</span>
                </div>
                <div className="text-text-muted">
                  Active: <span className="text-text">{currentChunkNumber || "-"}</span>
                </div>
                <div className="text-text-muted">
                  Audio ready: <span className="text-text">{preparedChunkCount}/{chunkPlan.length}</span>
                </div>
                <div className="text-text-muted">
                  Timestamps: <span className="text-text">{timestampReadyCount}/{chunkPlan.length}</span>
                </div>
              </div>

              <div className="space-y-1">
                {chunkPlan.length === 0 ? (
                  <div className="text-[10px] text-text-muted py-1">
                    No chunk plan yet. Start playback to populate.
                  </div>
                ) : (
                  chunkPlan.map((chunk) => {
                    const status = chunkStatuses.get(chunk.index) || {
                      audio: "pending" as const,
                      timing: "pending" as const,
                    };
                    const lineEnd = chunk.lineOffset + chunk.lineCount - 1;
                    const duration = chunkDurations[chunk.index] || 0;
                    const timingLabel =
                      status.timing === "pending" ? "pending" : status.timing;
                    return (
                      <div
                        key={chunk.index}
                        className={`rounded-md border px-2 py-1 ${
                          currentChunkIndex === chunk.index
                            ? "border-accent/50 bg-accent/10"
                            : "border-border bg-surface-alt/40"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[10px] text-text font-medium">
                            #{chunk.index + 1} · L{chunk.lineOffset}-{lineEnd}
                          </div>
                          <div className="text-[10px] text-text-muted">
                            {duration > 0 ? fmt(duration) : "~"}
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-[10px] mt-0.5">
                          <div className={audioStatusTone(status.audio)}>
                            audio: {audioStatusLabel(status.audio)}
                          </div>
                          <div className={timingStatusTone(status.timing)}>
                            stt: {timingLabel}
                          </div>
                        </div>
                        <div className="text-[10px] text-text-muted mt-0.5 truncate">
                          {chunk.text.replace(/\s+/g, " ").trim().slice(0, 56) || "(empty chunk)"}
                        </div>
                        {status.note && (
                          <div className="text-[10px] text-text-muted/90 truncate">
                            {status.note}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => {
            stop();
            onClose();
          }}
          className="text-text-muted hover:text-text transition-colors ml-1"
          title="Close player"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
